import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, delay, from, map, mergeMap, of, withLatestFrom } from 'rxjs';

import { ApiService } from '../../services/api/api.service';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import { UtilsService } from '../../services/utils/utils.service';
import {
    AlertError,
    BundleRoleCrudOperataions,
    GetAllRolePermissions,
    GetBundlePermissions,
    GetBundleRolePermissions,
    GetPermissionsByTypes,
    GetRolePermissions,
    SetAllRolePermissions,
    SetBundlePermissions,
    SetBundleRolePermissions,
    SetPermissionsByTypes,
    SetRolePermissions,
    UpdateBundleRolePermissions,
} from '../actions';
import {
    getAllRolePermissions$,
    getBundlePermissions$,
    getBundleRolePermissions$,
    getCurrentOrganizationId$,
    getRolePermissions$,
    getSelectedRoleId$,
    getSessionUserPermissions$,
} from '../selectors';

@Injectable()
export class PermissionsEffects {
    constructor(
        private actions$: Actions,
        private _api: ApiService,
        private store$: Store,
        private Utils: CommonUtilsService,
        private _utils: UtilsService
    ) {}

    getPermissionsByTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GetPermissionsByTypes),
            delay(10),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$), this.store$.select(getSessionUserPermissions$)),
            mergeMap(([_action, currentOrganizationId, sessionUserPermissions]) => {
                const permissionTypes = [];
                let categories = [];
                _action.permissionTypes.forEach(permissionType => {
                    if (sessionUserPermissions?.[permissionType] === undefined) {
                        permissionTypes.push(permissionType);
                        categories = _action?.categories;
                    } else if (_action?.categories?.length > 0) {
                        _action.categories.forEach(serviceId => {
                            if (sessionUserPermissions?.[permissionType]?.[serviceId] === undefined) {
                                permissionTypes.push(permissionType);
                                categories.push(serviceId);
                            }
                        });
                    }
                });
                if (permissionTypes?.length === 0 && categories?.length === 0) {
                    return [];
                }
                return from(this._api.permissions.getPermissionsByCriteria(permissionTypes, categories, _action.noAlerts)).pipe(
                    map(event => {
                        return SetPermissionsByTypes({
                            currentOrganizationId,
                            permissions: CommonUtilsService.cloneObject(event || []),
                        });
                    }),
                    catchError(() => {
                        return [];
                    })
                );
            })
        )
    );

    getBundlePermissions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GetBundlePermissions),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$), this.store$.select(getBundlePermissions$)),
            mergeMap(([action, organizationId, bundlePermissions]) => {
                if (bundlePermissions !== undefined && !this.Utils.isEmpty(bundlePermissions)) {
                    return [];
                }
                return from(this._api.roles.getBundlePermissions(action.bundleId, action.selectedApps)).pipe(
                    map(res => {
                        return SetBundlePermissions({
                            organizationId,
                            bundleId: action.bundleId,
                            permissions: res?.response?.permissions,
                        });
                    }),
                    catchError(e => of(AlertError({ message: e?.msg })))
                );
            })
        )
    );

    getBundleRolePermissions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GetBundleRolePermissions),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$), this.store$.select(getBundleRolePermissions$)),
            mergeMap(([action, organizationId, bundleRolePermissions]) => {
                if (bundleRolePermissions?.length > 0) {
                    return of(
                        SetBundleRolePermissions({
                            organizationId,
                            bundleId: action.bundleId,
                            roleId: action.roleId,
                            rolePermissions: CommonUtilsService.cloneObject(bundleRolePermissions),
                        })
                    );
                }
                return from(this._api.roles.getBundleRolePermissions(action.bundleId, action.roleId)).pipe(
                    map(res => {
                        return SetBundleRolePermissions({
                            organizationId,
                            bundleId: action.bundleId,
                            roleId: action.roleId,
                            rolePermissions: res?.response?.permissions,
                        });
                    }),
                    catchError(e => of(AlertError({ message: e?.msg })))
                );
            })
        )
    );

    UpdateBundleRolePermissions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpdateBundleRolePermissions),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$), this.store$.select(getSelectedRoleId$)),
            mergeMap(([action, organizationId, roleId]) => {
                const permissions = action.permissions.reduce((permissionIds, permission) => {
                    permissionIds.push({ uuid: permission.uuid });
                    return permissionIds;
                }, []);
                return from(this._api.roles.updateBundleRolePermissions(action.bundleId, roleId, permissions)).pipe(
                    map(res => {
                        this.store$.dispatch(
                            BundleRoleCrudOperataions({
                                msg: res?.msg,
                                actionType: action?.actionType || 'UPDATE',
                            })
                        );
                        const updatedPermissions = [];
                        action.permissions.forEach(permission => {
                            updatedPermissions.push(permission.uuid);
                        });
                        return SetBundleRolePermissions({
                            organizationId,
                            bundleId: action.bundleId,
                            roleId: roleId,
                            rolePermissions: updatedPermissions,
                        });
                    }),
                    catchError(e => of(AlertError({ message: e?.msg })))
                );
            })
        )
    );

    getRolePermissions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GetRolePermissions),
            withLatestFrom(
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getRolePermissions$),
                this.store$.select(getSelectedRoleId$)
            ),
            mergeMap(([action, organizationId, rolePermissions, roleId]) => {
                if (rolePermissions?.length > 0) {
                    return of(
                        SetRolePermissions({
                            organizationId,
                            roleId: roleId,
                            rolePermissions: CommonUtilsService.cloneObject(rolePermissions),
                        })
                    );
                }
                return from(this._api.roles.getRolePermissions(action.role)).pipe(
                    map((res: any) => {
                        return SetRolePermissions({
                            organizationId,
                            roleId: roleId,
                            rolePermissions: res,
                        });
                    }),
                    catchError(e => of(AlertError({ message: e?.msg })))
                );
            })
        )
    );

    getAllRolePermissions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GetAllRolePermissions),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$), this.store$.select(getAllRolePermissions$)),
            mergeMap(([_action, organizationId, rolePermissions]) => {
                if (rolePermissions !== null && rolePermissions !== undefined) {
                    return of(
                        SetAllRolePermissions({
                            organizationId,
                            permissions: CommonUtilsService.cloneObject(rolePermissions),
                        })
                    );
                }
                return from(this._api.roles.getAllPermissions(organizationId)).pipe(
                    map((res: any) => {
                        const permissions = this._utils.copyObjectToObject(res[0]?.response?.permissions, res[1]?.response?.permissions);
                        return SetAllRolePermissions({
                            organizationId,
                            permissions: permissions,
                        });
                    }),
                    catchError(e => of(AlertError({ message: e?.msg })))
                );
            })
        )
    );
}
